import { Field, Label } from '@headlessui/react';
import { FC } from 'react';

import { Checkbox } from '../../../../components/checkbox/index';
import { DatePicker } from '../../../../components/date-picker/index';
import { TableRow as TableRowBase } from '../../../../components/table/index';
import { useFormat, useLocale } from '../../../../hooks/index';
import { SharePlanType, sharePlanTypeTitle } from '../../../../types/pool-plans.types';
import {
  ApproveMilestoneDto,
  TargetBasedVestingMilestoneItem,
} from '../../../../types/vesting-tasks.types';
import { calcMilestoneIssuedShares } from '../../../../utils/calc-milestone-issued-shares.util';
import { ApproveTargetBasedColumn, approveTargetBasedColumnMap } from './approve-target.types';

export type TableRowProps = {
  grantId: string;
  id: string;
  isSelected: boolean;
  milestone: TargetBasedVestingMilestoneItem;
  milestoneIndex: number;
  milestones: TargetBasedVestingMilestoneItem[];
  onRemoveSelect: (stakeholderId: string) => void;
  onSelect: (data: Except<ApproveMilestoneDto, 'milestoneIndex'>) => void;
  planName: string;
  planType: SharePlanType;
  shareClassName: string;
  stakeholderName: string;
  totalSharesCount: number;
  totalVested: number;
};

export const TableRow: FC<TableRowProps> = ({
  grantId,
  id,
  isSelected,
  milestone: { name, targetDate, weight },
  milestoneIndex,
  milestones,
  onRemoveSelect,
  onSelect,
  planName,
  planType,
  shareClassName,
  stakeholderName,
  totalSharesCount,
  totalVested,
}) => {
  const { messagesLocale } = useLocale();
  const { format } = useFormat();

  const issuedShares = calcMilestoneIssuedShares({
    milestoneIndex,
    milestones,
    totalSharesCount,
  });

  return (
    <TableRowBase
      columnOrder={approveTargetBasedColumnMap}
      contentClassName="pl-4"
      fields={{
        [ApproveTargetBasedColumn.APPROVAL_DATE]: (
          <DatePicker className="border-b border-gray-700 bg-gray-50 px-3 py-2" />
        ),
        [ApproveTargetBasedColumn.GRANT]: grantId.split('-')[0].toUpperCase(),
        [ApproveTargetBasedColumn.ISSUED_SHARES]: issuedShares,
        [ApproveTargetBasedColumn.PLAN_NAME]: planName,
        [ApproveTargetBasedColumn.PLAN_TYPE]: messagesLocale[sharePlanTypeTitle[planType]],
        [ApproveTargetBasedColumn.SELECT]: (
          <Field className="flex h-full w-full items-center">
            <Checkbox
              checked={isSelected}
              id={id}
              onChange={(checked) => {
                checked
                  ? onSelect({
                      value: issuedShares,
                      vestingTaskId: id,
                    })
                  : onRemoveSelect(id);
              }}
            />
            <Label className="absolute left-0 top-0 h-full w-full cursor-pointer" htmlFor={id} />
          </Field>
        ),
        [ApproveTargetBasedColumn.SHARE_CLASS]: shareClassName,
        [ApproveTargetBasedColumn.STAKEHOLDER]: stakeholderName,
        [ApproveTargetBasedColumn.TARGET_DATE]: format(targetDate, 'dd/MM/yyyy'),
        [ApproveTargetBasedColumn.TARGET_NAME]: name,
        [ApproveTargetBasedColumn.VESTED_PERCENTAGE]: Math.round(totalVested * 100),
        [ApproveTargetBasedColumn.VESTING_WEIGHT]: `${Math.round(weight * 100)}%`,
        id,
      }}
      trClassName={isSelected && 'bg-gray-50'}
    />
  );
};
